import { lazy, type ComponentType, type LazyExoticComponent } from 'react';

/**
 * Stale-chunk recovery for lazy-loaded routes.
 *
 * After a deploy, an already-loaded bundle requests code-split chunks whose
 * hashed filenames no longer exist on the server. Vite wraps every lazy
 * `import()` in `__vitePreload`, which dispatches a `vite:preloadError` event
 * on failure. Our handler in `main.tsx` calls `event.preventDefault()` to
 * trigger a one-time reload — but that also makes Vite *swallow* the rejection,
 * so `__vitePreload(...)` fulfils with `undefined` instead of rejecting.
 *
 * A naive `.then(m => ({ default: m.SomePage }))` then throws
 * `TypeError: Cannot read properties of undefined (reading 'SomePage')`.
 * `resolveLazyModule` absorbs that case: it reloads once and returns a
 * never-settling promise so `<Suspense>` simply stays in its fallback while
 * the page navigates away.
 */

const STALE_CHUNK_RELOAD_AT = 'stale-chunk-reload-at';

/** Suppress a *repeat* reload for this long, so a bad build can't reload-loop. */
const RELOAD_COOLDOWN_MS = 30_000;

/**
 * Reload the page to pick up a fresh build, unless we already reloaded for a
 * stale chunk within the cooldown window. The cooldown prevents an infinite
 * loop when the reload itself does not fix the staleness (e.g. a CDN- or
 * service-worker-cached `index.html`), while still allowing recovery from a
 * genuinely new deploy later in the same session.
 */
export function reloadOnceForStaleChunk(): void {
  const lastReloadAt = Number(sessionStorage.getItem(STALE_CHUNK_RELOAD_AT) ?? 0);
  if (Date.now() - lastReloadAt < RELOAD_COOLDOWN_MS) return;
  sessionStorage.setItem(STALE_CHUNK_RELOAD_AT, String(Date.now()));
  window.location.reload();
}

type LazyPayload = { default: ComponentType<unknown> };

/**
 * Map a resolved dynamic-import module to a `React.lazy` payload.
 * If the module — or its named export — is missing (a swallowed stale-chunk
 * preload), trigger a reload and return a promise that never settles.
 */
export function resolveLazyModule<M, K extends keyof NonNullable<M>>(
  mod: M,
  exportName: K,
): LazyPayload | Promise<never> {
  const component = mod == null ? undefined : (mod as NonNullable<M>)[exportName];
  if (!component) {
    reloadOnceForStaleChunk();
    return new Promise<never>(() => {});
  }
  return { default: component as ComponentType<unknown> };
}

/**
 * `React.lazy` for a module's named export, with stale-chunk recovery.
 *
 * Replaces `lazy(() => import('./X').then(m => ({ default: m.X })))`.
 *
 * #908 — the return type preserves the wrapped component's own props (the
 * internal LazyPayload erases them to `unknown` for the stale-chunk dance),
 * so routes can pass props like `<CustomersListPage leadsOnly />`.
 */
export function lazyPage<M, K extends keyof NonNullable<M>>(
  importFn: () => Promise<M>,
  exportName: K,
) {
  return lazy(
    () => importFn().then((mod) => resolveLazyModule(mod, exportName)),
  ) as NonNullable<M>[K] extends ComponentType<infer P>
    ? LazyExoticComponent<ComponentType<P>>
    : LazyExoticComponent<ComponentType<unknown>>;
}
